import type {
    GridColDef,
    GridRowId,
    GridValidRowModel,
} from '@mui/x-data-grid';
import type { MutableRefObject, RefObject } from 'react';
import type { GridApiCommunity } from '@mui/x-data-grid/internals';
import type { GridDataSource as GridDataSourcePremium } from '@mui/x-data-grid-premium';
import { getAttributeServiceUrl } from './ReduxHelper';
import { featureServiceGridDataSource } from '../services/FeatureServiceGridDataSource.ts';
import { mapEsriFieldsToDataGridColumns, type GridFieldConfiguration } from './GridColumnHelper.ts';
import { getEsriFieldsFromUrl } from './MapUtilities.tsx';
import { darken, type Theme } from '@mui/system';
export interface DataSourceAndColumns {
    dataSource: GridDataSourcePremium;
    colDefs: GridColDef[];
}
export interface UnsavedChanges {
    unsavedRows: Record<GridRowId, GridValidRowModel>;
    rowsBeforeChange: Record<GridRowId, GridValidRowModel>;
}

export interface BulkEditingContextValue {
    unsavedChangesRef: RefObject<UnsavedChanges>;
    setHasUnsavedRows: (hasUnsavedRows: boolean) => void;
}

export function handleProcessRowUpdate(
    newRow: GridValidRowModel,
    oldRow: GridValidRowModel,
    unsavedChangesRef: RefObject<UnsavedChanges>,
    setHasUnsavedRows: (hasUnsavedRows: boolean) => void
) {
    if (!unsavedChangesRef.current) {
        return newRow ? newRow : oldRow;
    }
    const rowId = newRow.rowId;

    unsavedChangesRef.current.unsavedRows[rowId] = newRow;
    if (!unsavedChangesRef.current.rowsBeforeChange[rowId]) {
        unsavedChangesRef.current.rowsBeforeChange[rowId] = oldRow;
    }
    setHasUnsavedRows(true);

    return newRow;
}

export function handleDiscardChanges(
    unsavedChangesRef: { current: { rowsBeforeChange: any; unsavedRows?: {} } },
    setHasUnsavedRows: (arg0: boolean) => void,
    apiRef: MutableRefObject<GridApiCommunity | null>
) {
    setHasUnsavedRows(false);
    Object.values(unsavedChangesRef.current.rowsBeforeChange).forEach((row: any) => {
        apiRef.current?.updateRows([row]);
    });
    unsavedChangesRef.current = {
        unsavedRows: {},
        rowsBeforeChange: {},
    };
}

export function handleGetRowClassName(
    id: string | number,
    unsavedChangesRef: { current: { unsavedRows: { [x: string]: any } } }
) {
    const unsavedRow = unsavedChangesRef.current.unsavedRows[id];
    if (unsavedRow) {
        if (unsavedRow._action === 'delete') {
            return 'row--removed';
        }
        return 'row--edited';
    }
    return '';
}

export function handleGetSubRowClassName(id: string | number, field: string, rowEditedCells: any) {
    const currentRow = rowEditedCells.existingRow[id];
    const unsavedRow = rowEditedCells.newRow.find((r: any) => r.rowId === id);
    if (currentRow && unsavedRow && unsavedRow[field] && currentRow[field] !== unsavedRow[field]) {
        return 'cell--edited';
    }
    return '';
}

export function handleGetParentRowClassName(id: any, field: string, parentRowEditedCells: any) {
    const currentRow = parentRowEditedCells.existingRow;
    const unsavedRow = parentRowEditedCells.newRow;

    if (id !== currentRow.rowId) return '';

    if (currentRow && unsavedRow && field in unsavedRow) {
        const oldVal = currentRow[field];
        const newVal = unsavedRow[field];
        // Treat null, undefined, and empty string as equivalent "empty" values
        // so previously-empty fields don't get highlighted when they remain empty
        const isEmpty = (v: any) => v === null || v === undefined || v === '';
        if (isEmpty(oldVal) && isEmpty(newVal)) return '';
        if (oldVal !== newVal) return 'cell--edited';
    }
    return '';
}

export const configureDataSourceAndFields = async (
    layerId: string,
    bulkEditFields: GridFieldConfiguration[],
    baseWhereClause: string = '',
    uidField: string = '',
    unsavedChangesRef?: { current: { unsavedRows: Record<string, any> } },
    alwaysFetchData = false

): Promise<DataSourceAndColumns> => {
    const rootServiceUrl = getAttributeServiceUrl();
    const attributesUrl = `${rootServiceUrl}/${layerId}`;

    if (!attributesUrl) {
        throw new Error('Unable to determine attributes URL for bulk edit layer');
    }
    const esriFields = await getEsriFieldsFromUrl(attributesUrl);

    return {
        dataSource: featureServiceGridDataSource(
            attributesUrl,
            baseWhereClause,
            uidField,
            unsavedChangesRef,
            alwaysFetchData
        ),
        colDefs: mapEsriFieldsToDataGridColumns(esriFields, bulkEditFields),
    };
};

export const styleEditedRow = (theme: Theme) => {
    if (theme.palette.mode === 'light') {
        return 'rgba(255, 254, 176, 0.3)';
    }
    return darken('rgba(255, 254, 176, 1)', 0.6);
};